import { randomHash } from 'src/helpers';
import { IAction } from 'src/typings';
import { AIChatMessage, AIChatState, AIChatSuccessPayload } from 'src/typings/ai-chat';
import { ActionTypes } from './actions';
import {
  advanceProgressTraceState,
  createProgressTrace,
  finishProgressTraceState,
} from './progress';

export const initialState: AIChatState = {
  isOpen: false,
  draft: '',
  status: 'idle',
  messages: [],
  error: null,
  conversationId: null,
  conversationContext: null,
  activeTrace: null,
};

const hasUserMessage = (messages: AIChatMessage[]) => (
  messages.some(({ role }) => role === 'user')
);

const hasFailedProgressTrace = (state: AIChatState) => (
  state.activeTrace?.steps.some((step) => step.state === 'failed') === true
);

export const aiChatReducer = (
  state = initialState,
  { type, payload }: IAction,
): AIChatState => {
  switch (type) {
    case ActionTypes.OPEN_CHAT:
      return { ...state, isOpen: true };

    case ActionTypes.MINIMIZE_CHAT:
      return { ...state, isOpen: false };

    case ActionTypes.CLOSE_CHAT:
      return initialState;

    case ActionTypes.NEW_CHAT:
      return { ...initialState, isOpen: true };

    case ActionTypes.SET_DRAFT:
      return { ...state, draft: payload };

    case ActionTypes.SEND_MESSAGE_REQUEST:
      return {
        ...state,
        draft: '',
        status: 'thinking',
        messages: [
          ...state.messages,
          { id: randomHash(), role: 'user', text: payload },
        ],
        error: null,
        activeTrace: hasFailedProgressTrace(state) ? null : state.activeTrace,
      };

    case ActionTypes.SEND_MESSAGE_SUCCESS: {
      const response = payload as AIChatSuccessPayload;
      const progressTrace = response.includeProgressTrace
        && state.activeTrace?.usedTool
        && state.activeTrace.finishedAt
        ? state.activeTrace
        : undefined;
      const assistantMessage = {
        ...response.message,
        role: 'assistant' as const,
        resultsExpanded: false,
        ...(progressTrace ? { progressTrace } : {}),
      };
      return {
        ...state,
        status: 'idle',
        conversationId: response.conversationId ?? state.conversationId,
        conversationContext: response.conversationContext ?? state.conversationContext,
        messages: [
          ...state.messages,
          assistantMessage,
        ],
        error: null,
        activeTrace: null,
      };
    }

    case ActionTypes.SEND_MESSAGE_FAILURE:
      return { ...state, status: 'error', error: payload };

    case ActionTypes.CONTEXT_LIMIT_REACHED:
      return {
        ...state,
        status: 'context_limit_reached',
        error: null,
        activeTrace: null,
      };

    case ActionTypes.SEED_FAILED_MESSAGE:
      return {
        ...state,
        draft: '',
        status: 'error',
        messages: [
          ...state.messages,
          { id: randomHash(), role: 'user', text: payload.text },
        ],
        error: payload.error,
      };

    case ActionTypes.RETRY_LAST_MESSAGE: {
      const retrying = hasUserMessage(state.messages);
      return {
        ...state,
        status: retrying ? 'thinking' : 'idle',
        error: null,
        activeTrace: retrying && hasFailedProgressTrace(state) ? null : state.activeTrace,
      };
    }

    case ActionTypes.TOGGLE_MESSAGE_RESULTS: {
      const target = state.messages.find(({ id, role }) => id === payload && role === 'assistant');
      if (!target) return state;

      return {
        ...state,
        messages: state.messages.map((message) => (
          message.id === payload && message.role === 'assistant'
            ? { ...message, resultsExpanded: !message.resultsExpanded }
            : message
        )),
      };
    }

    case ActionTypes.INVALIDATE_CONVERSATION:
      return { ...state, conversationId: null, conversationContext: null };

    case ActionTypes.SET_CONVERSATION_ID:
      return typeof payload === 'string'
        ? { ...state, conversationId: payload }
        : {
          ...state,
          conversationId: payload.conversationId,
          conversationContext: payload.conversationContext,
        };

    case ActionTypes.START_PROGRESS_TRACE:
      return { ...state, activeTrace: createProgressTrace(payload) };

    case ActionTypes.ADVANCE_PROGRESS_TRACE: {
      const { activeTrace } = state;
      if (!activeTrace || activeTrace.id !== payload.traceId) return state;
      const nextActiveTrace = advanceProgressTraceState(activeTrace, payload);
      return nextActiveTrace === activeTrace ? state : { ...state, activeTrace: nextActiveTrace };
    }

    case ActionTypes.COMPLETE_PROGRESS_TRACE:
    case ActionTypes.FAIL_PROGRESS_TRACE: {
      const { activeTrace } = state;
      if (!activeTrace || activeTrace.id !== payload.traceId) return state;
      const nextActiveTrace = finishProgressTraceState(
        activeTrace,
        type === ActionTypes.COMPLETE_PROGRESS_TRACE ? 'completed' : 'failed',
        payload.finishedAt,
      );
      return nextActiveTrace === activeTrace ? state : { ...state, activeTrace: nextActiveTrace };
    }

    case ActionTypes.CLEAR_PROGRESS_TRACE:
      return state.activeTrace?.id === payload.traceId
        ? { ...state, activeTrace: null }
        : state;

    default:
      return state;
  }
};
